import { BaseDialogComponent } from './../../../../../components/kit/base-dialog/base-dialog.component';
import {
    PersonAddressEnum, personAddressEnumOptions,
} from './../../../../../../../../types/src/services/enum/person-address.enum';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { fb } from '../../../../shared/shared.global-service';
import { AddressDetailInput, PersonAddressGridItemObject } from '@it2go/types';
import { removeTypename } from '../../../../shared/utils/object.utils';
import {
    PersonAddressUpdateInput,
} from '@libs/types/src/services/input-type/person/address/person-address.update-input';
import {
    PersonAddressCreateInput,
} from '@libs/types/src/services/input-type/person/address/person-address.create-input';
import { dateify } from '../../../../shared/helpers/date.helper';
import { PersonAddressDialogStore } from './person-address-dialog.store';
import { endOfDay } from '../../../../../utils/date.utils';
import { contentGlobalSuccess } from '../../../content.error';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { RuianService } from '../../../../shared/components/map/ruian.service';

@Component({
    selector: 'app-person-address-dialog',
    templateUrl: './person-address-dialog.component.html',
    styleUrls: ['./person-address-dialog.component.scss'],
    providers: [PersonAddressDialogStore, RuianService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AddressInputComponent,
        DateInputComponent,
        SelectInputComponent,
        TranslatePipe,
    ],
})
export class PersonAddressDialogComponent extends BaseDialogComponent implements OnInit {

    protected readonly store = inject(PersonAddressDialogStore);
    private readonly ruianService = inject(RuianService);

    protected personId: number | null = null;
    protected address: PersonAddressGridItemObject | null = null;
    protected err: string | null = null;

    protected form = fb.group({
        type: fb.control<string | null>(null, Validators.required),
        address: fb.control<AddressDetailInput | null>(null),
        validFrom: fb.control<Date | null>(new Date()),
        validTo: fb.control<Date | null>(null),
    });

    public ngOnInit(): void {
        this.address = this.dialogConfig.data?.address;
        this.personId = this.dialogConfig.data?.personId;

        const item = this.dialogConfig.data?.address;

        if (item) {
            this.form.patchValue({
                type: item.type,
                address: item.address,
                validFrom: dateify(item.validFrom),
                validTo: dateify(item.validTo),
            });
        }
    }

    public override async save(): Promise<void> {
        if (isInvalid(this.form)) return;

        const { validTo, validFrom } = this.form.value;
        if (validTo && validFrom && validFrom > validTo) {
            this.err = 'errors.DATE_FROM_PRECEDES_DATE_TO';
            return;
        }

        const address = await this.appendOrp(removeTypename(this.form.value.address));
        const _data = {
            ...this.form.value,
            address,
        } as any;

        if (this.address?.id) {
            const data = _data as PersonAddressUpdateInput;
            data.id = this.address?.id;
            this.store.api.updatePersonAddress(data);
            contentGlobalSuccess.next('common.updated');
        } else if (this.personId) {
            const data = _data as PersonAddressCreateInput;
            data.personId = this.personId;
            this.store.api.createPersonAddress(data);
            contentGlobalSuccess.next('common.created');
        }

        this.dialogRef.close(_data);
    }

    private async appendOrp(address: AddressDetailInput | null): Promise<AddressDetailInput | null> {
        if (!address || this.form.value.type !== PersonAddressEnum.PERMANENT_RESIDENCE) return address;

        const orp = await this.ruianService.getOrp(address.position);
        if (!orp) return address;

        return {
            ...address,
            regionalStructure: [
                ...address.regionalStructure.filter((item) => item.type !== 'regional.orp'),
                { name: orp.nazev, type: 'regional.orp', isoCode: null },
            ],
        };
    }

    protected readonly personAddressEnumOptions = personAddressEnumOptions;

    protected readonly endOfDay = endOfDay;
}
